import { t } from "i18next";
import moment from "moment";
import React, { useState } from "react";
import {
  GeneralCalendar,
  Loader,
  GeneralButton,
} from "../../../../../components";
import { useFormContext } from "react-hook-form";
import EmptyImage from "../../../../../assets/images/icons/appointment-empty.svg";
import { EndPoints } from "../../../../../Constants/endpoints";
import {
  IAvailableScheduled,
  IScheduledDays,
  IScheduledTime,
} from "../../../../../interface/appointmentInterfaces";
import { useGetData } from "../../../../../hooks";

const FollowUpAppointment = ({
  name,
  fieldsConfig,
}: {
  name: string;
  fieldsConfig: any;
}) => {
  const methods = useFormContext();

  const appointmentDate = methods.getValues(name);

  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [appointmentTime, setAppointmentTime] = useState<string>();
  const { data } = useGetData<IScheduledDays[]>(EndPoints.GetScheduledDays);

  const selectedMoment = appointmentDate
    ? moment(appointmentDate, "YYYY-MM-DD HH:mm")
    : moment();

  const selectedDateForQuery = selectedMoment.locale("en").format("YYYY-MM-DD");

  const { data: availableScheduledData, isLoading } =
    useGetData<IAvailableScheduled>(EndPoints.GetAvailableScheduled, {
      filter: { date: selectedDateForQuery },
    });

  const ScheduledDaysArray = data?.Data?.map((item: any) => item?.Name) ?? [];

  const disabledClassname =
    "!bg-Gray !text-body-text !cursor-default opacity-30 font-normal !shadow-none";
  const selectedClassname = "!bg-systemBg !text-primary !shadow-none";

  const handleSelectDate = (dateStr: string) => {
    const date = moment(dateStr).toDate();

    const isPastDay = moment(date).isBefore(moment(), "day");
    const dayOfWeek = moment(date).format("dddd");
    const noSchedule = !ScheduledDaysArray.includes(dayOfWeek);

    if (isPastDay || noSchedule) return;

    const newDateStr = moment(date).locale("en").format("YYYY-MM-DD");

    setIsScheduleLoading(true);
    methods.setValue(name, newDateStr);

    setTimeout(() => setIsScheduleLoading(false), 300);
  };

  const isPastTime = (timeStr: string) => {
    const selectedDay = appointmentDate
      ? moment(appointmentDate, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD"])
      : moment();

    const isToday = selectedDay.isSame(moment(), "day");
    if (!isToday) return false;

    return moment(timeStr, "HH:mm:ss").isBefore(moment());
  };

  return (
    <div className="mx-auto flex max-h-[392px] w-[50vw] flex-col gap-4 overflow-hidden md:flex-row">
      <GeneralCalendar
        className="!w-[20vw]"
        onSelectedDate={handleSelectDate}
        customCellClassname={(date) => {
          const dayOfWeek = moment().day(date.getDay()).format("dddd");
          const isPastDay = moment(date).isBefore(moment(), "day");
          const noSchedule = !ScheduledDaysArray.includes(dayOfWeek);

          return isPastDay || noSchedule
            ? "!cursor-default !bg-Gray !text-body-text font-normal disabled"
            : "p-2";
        }}
        customHeadClassname={(date) => {
          const dayOfWeek = moment().day(date.getDay()).format("dddd");
          const classes = "";
          return ScheduledDaysArray.includes(dayOfWeek) ? classes : "p-2";
        }}
      />

      {appointmentDate && (
        <div className="flex flex-1 flex-col gap-2">
          {isLoading || isScheduleLoading ? (
            <div className="mb-3 flex w-full items-center justify-center">
              <Loader />
            </div>
          ) : (availableScheduledData?.Data?.Schedules?.length ?? 0) > 0 ? (
            <div className="show-scrollbar mb-3 grid w-[30vw] auto-rows-max grid-cols-2 gap-2 overflow-auto px-2 py-2 md:grid-cols-3 xl:grid-cols-3">
              {availableScheduledData?.Data.Schedules.map(
                (item: IScheduledTime, index: number) => {
                  const isDisabled = !item.isAvailable || isPastTime(item.time);

                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (isDisabled) return;

                        const dayStr = moment(appointmentDate, [
                          "YYYY-MM-DD HH:mm",
                          "YYYY-MM-DD",
                        ])
                          .locale("en")
                          .format("YYYY-MM-DD");

                        const finalDateTimeStr = moment(
                          `${dayStr} ${item.time}`,
                          "YYYY-MM-DD HH:mm:ss",
                        )
                          .locale("en")
                          .format("YYYY-MM-DD HH:mm");

                        methods.setValue(name, finalDateTimeStr);
                        setAppointmentTime(item.time);
                      }}
                      className={`box text-md flex h-[2.5rem] cursor-pointer items-center justify-center text-nowrap font-bold text-secondaryDark !shadow-[0px_7px_17.1px_0px_#163CFF14] hover:!bg-systemBg hover:!text-primary hover:!shadow-none ${
                        item.time === appointmentTime ? selectedClassname : ""
                      } ${isDisabled ? disabledClassname : ""}`}
                    >
                      {moment(item.time, "HH:mm:ss").format("hh:mm A")}
                    </div>
                  );
                },
              )}
            </div>
          ) : (
            <div className="mb-3 flex h-[20dvh] max-h-[20dvh] w-full flex-col items-center justify-center">
              <img className="max-w-[6rem]" alt="empty" src={EmptyImage} />
              <div className="text-body-text text-base">
                {t("no_available_schedule_in_this_day")}
              </div>
            </div>
          )}
          <GeneralButton
            color="lite"
            title={t("skip")}
            onClick={() => {
              methods.setValue(name, undefined);
              setAppointmentTime(undefined);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default FollowUpAppointment;
